import { useMemo, useRef, useState } from 'react'
import { NFSE_STATUS } from '../../../../constants/fiscal'
import {
  getNfses,
  postCancelarNfse,
  postEmailNfse,
  postSincronizarNfse,
} from '../../../../services/api/https/fiscal/nfse.http'
import { urlDownloadNfse } from '../../../../services/api/routes/fiscal.routes'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH: 'Buscando NFS-e',
  SYNC: 'Sincronizando status da NFS-e',
  CANCEL: 'Cancelando NFS-e',
  EMAIL: 'Enviando e-mail da NFS-e',
}

const NOMES_STATUS = {
  [NFSE_STATUS.PROCESSANDO]: 'em processamento',
  [NFSE_STATUS.AUTORIZADO]: 'autorizada',
  [NFSE_STATUS.ERRO_AUTORIZACAO]: 'não autorizada',
  [NFSE_STATUS.CANCELADO]: 'cancelada',
}

const estadoInicial = () => ({
  errorMessage: null,
  successMessage: null,
  loadingMessage: LOAD_MESSAGE.NONE,
  nfses: [],
  filtradas: [],
  filtroStatus: [],
  filtroEmissores: [],
  selecionarTodos: false,
  periodo: getPeriodoMesAtual(),
  modalCancelar: { visible: false, nfse: null },
  modalEmail: { visible: false, nfse: null },
  modalEmitir: { visible: false, contexto: null },
})

const aplicarFiltros = (nfses, { filtroStatus, filtroEmissores }) =>
  nfses.filter(
    nfse =>
      (!filtroStatus.length || filtroStatus.includes(nfse.status)) &&
      (!filtroEmissores.length || filtroEmissores.includes(nfse.emissor_id)),
  )

export default function useGestaoNfse() {
  const [state, setState] = useState(estadoInicial)
  const periodoRef = useRef(state.periodo)

  const localDispatch = useMemo(() => {
    const falhar = error =>
      setState(prev => ({
        ...prev,
        errorMessage: errorHandler(error),
        loadingMessage: LOAD_MESSAGE.NONE,
      }))

    const buscar = async (tpId, pInicial, pFinal) => {
      if (!tpId) {
        setState(prev => ({ ...prev, nfses: [], filtradas: [] }))
        falhar('Selecione um Operador Logístico')
        return
      }
      if (!pInicial || !pFinal) {
        falhar('Período inicial e final são obrigatórios')
        return
      }
      try {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCH }))
        const nfses = await getNfses(tpId, pInicial, pFinal)
        periodoRef.current = { dataInicial: pInicial, dataFinal: pFinal }
        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.NONE,
          nfses,
          filtradas: aplicarFiltros(nfses, prev),
          selecionarTodos: false,
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
        }))
      } catch (error) {
        falhar(error)
      }
    }

    const executar = async (mensagem, tpId, acao, sucesso) => {
      try {
        setState(prev => ({ ...prev, loadingMessage: mensagem }))
        const resultado = await acao()
        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.NONE,
          successMessage: sucesso(resultado),
        }))
        buscar(tpId, periodoRef.current.dataInicial, periodoRef.current.dataFinal)
      } catch (error) {
        falhar(error)
      }
    }

    return {
      resetMessages() {
        setState(prev => ({ ...prev, errorMessage: null, successMessage: null }))
      },

      onPesquisar(tpId, pInicial, pFinal) {
        buscar(tpId, pInicial, pFinal)
      },

      recarregar(tpId) {
        buscar(tpId, periodoRef.current.dataInicial, periodoRef.current.dataFinal)
      },

      onMenuItemClick(item, nfse, tpId) {
        const menus = {
          DANFSe: () => window.open(nfse.url_danfse, '_blank'),
          XML: () => window.open(nfse.caminho_xml, '_blank'),
          'XML Cancelamento': () => window.open(nfse.caminho_xml_cancelamento, '_blank'),
          'Consultar no portal': () => window.open(nfse.url, '_blank'),
          'Sincronizar Status': () =>
            executar(
              LOAD_MESSAGE.SYNC,
              tpId,
              () => postSincronizarNfse(tpId, nfse.id),
              atualizada =>
                `Status sincronizado: NFS-e ${
                  NOMES_STATUS[atualizada.status] || atualizada.status
                }`,
            ),
          'Reenviar E-mail': () =>
            setState(prev => ({ ...prev, modalEmail: { visible: true, nfse } })),
          'Cancelar NFS-e': () =>
            setState(prev => ({ ...prev, modalCancelar: { visible: true, nfse } })),
          'Ver Erros': () => falhar(nfse.mensagem || 'A Focus não informou o motivo da rejeição'),
          Duplicar: () =>
            setState(prev => ({
              ...prev,
              modalEmitir: { visible: true, contexto: { duplicar: nfse.id } },
            })),
        }
        if (menus[item]) menus[item]()
      },

      onCancelarClose() {
        setState(prev => ({ ...prev, modalCancelar: { visible: false, nfse: null } }))
      },

      onCancelarSave(tpId, nfse, justificativa) {
        setState(prev => ({ ...prev, modalCancelar: { visible: false, nfse: null } }))
        executar(
          LOAD_MESSAGE.CANCEL,
          tpId,
          () => postCancelarNfse(tpId, nfse.id, justificativa),
          () => 'NFS-e cancelada com sucesso',
        )
      },

      onEmailClose() {
        setState(prev => ({ ...prev, modalEmail: { visible: false, nfse: null } }))
      },

      onEmailSave(tpId, nfse, emails) {
        setState(prev => ({ ...prev, modalEmail: { visible: false, nfse: null } }))
        executar(
          LOAD_MESSAGE.EMAIL,
          tpId,
          () => postEmailNfse(tpId, nfse.id, emails),
          () => 'E-mail da NFS-e enviado',
        )
      },

      onEmitirClick(tpId) {
        if (!tpId) return falhar('Selecione um Operador Logístico')
        setState(prev => ({
          ...prev,
          modalEmitir: { visible: true, contexto: { origem: 'avulsa' } },
        }))
      },

      onEmitirClose() {
        setState(prev => ({ ...prev, modalEmitir: { visible: false, contexto: null } }))
      },

      onModalError(error) {
        falhar(error)
      },

      onItemClicked(nfse) {
        setState(prev => ({
          ...prev,
          filtradas: prev.filtradas.map(item =>
            item.id === nfse.id ? { ...item, selected: !item.selected } : item,
          ),
        }))
      },

      onSelecionarTodosChange(value) {
        setState(prev => ({
          ...prev,
          selecionarTodos: value,
          filtradas: prev.filtradas.map(item => ({ ...item, selected: value })),
        }))
      },

      onFiltroChange(campo, valor) {
        setState(prev => {
          const proximo = { ...prev, [campo]: valor || [] }
          return { ...proximo, filtradas: aplicarFiltros(prev.nfses, proximo) }
        })
      },

      onDownload(tpId, nfses) {
        const comArquivo = nfses.filter(nfse => nfse.caminho_xml || nfse.url_danfse)
        if (!comArquivo.length)
          return falhar('Selecione ao menos uma NFS-e autorizada ou cancelada')
        window.open(
          urlDownloadNfse(
            tpId,
            comArquivo.map(nfse => nfse.id),
          ),
          '_blank',
        )
      },
    }
  }, [])

  return { state, localDispatch }
}
